import moment from 'moment';
import type { IPipelineEvent } from '../type';

const MONTH_ONLY_PATTERN = /^\d{4}-\d{2}$/;
const FULL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Parse `YYYY-MM` or `YYYY-MM-DD`; month-only values default to the 1st. */
const parsePipelineDelayDate = (dateStr: string): moment.Moment | null => {
  if (MONTH_ONLY_PATTERN.test(dateStr)) {
    const parsed = moment(dateStr, 'YYYY-MM', true);
    return parsed.isValid() ? parsed.startOf('month') : null;
  }

  if (FULL_DATE_PATTERN.test(dateStr)) {
    const parsed = moment(dateStr, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }

  return null;
};

/** True when API provides a multi-date delay history. */
export const hasDelayedEventDates = (evt: IPipelineEvent): boolean => {
  return Array.isArray(evt.dates) && evt.dates.length > 1;
};

export const getDelayCount = (dates: string[]): number => {
  return Math.max(0, dates.length - 1);
};

export const getDelayGapLabel = (dates: string[]): string => {
  if (dates.length < 2) {
    return '';
  }

  const first = parsePipelineDelayDate(dates[0]);
  const last = parsePipelineDelayDate(dates[dates.length - 1]);
  if (!first || !last) {
    return '';
  }

  const monthDiff = last.diff(first, 'months');
  if (monthDiff >= 1) {
    return `${monthDiff} Month${monthDiff > 1 ? 's' : ''}`;
  }

  const dayDiff = last.diff(first, 'days');
  return `${dayDiff} Day${dayDiff !== 1 ? 's' : ''}`;
};

export const getDelaySummaryLabel = (dates: string[]): string => {
  const delayCount = getDelayCount(dates);
  const gapLabel = getDelayGapLabel(dates);
  if (!delayCount || !gapLabel) {
    return '';
  }

  const delayWord = delayCount === 1 ? 'Delay' : 'Delays';
  return `${delayCount} ${delayWord} · ${gapLabel}`;
};
